import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  CheckCircle, AlertCircle, RefreshCw, Database,
  Mail, Server, Cpu, HardDrive, Activity, Play, Loader2
} from 'lucide-react';

function StatusBadge({ ok, sim = 'Ativo', nao = 'Não configurado' }) {
  return ok
    ? <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full"><CheckCircle size={10}/>{sim}</span>
    : <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full"><AlertCircle size={10}/>{nao}</span>;
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-500">{label}</span>
      <span className={`text-xs font-semibold text-gray-700 ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</span>
    </div>
  );
}

export default function AdminDebug() {
  const [dados,      setDados]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [testEmail,  setTestEmail]  = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [testingDb,  setTestingDb]  = useState(false);
  const [dbResult,   setDbResult]   = useState(null);
  const [endpoint,   setEndpoint]   = useState('');
  const [endpointRes, setEndpointRes] = useState(null);
  const [testingEndpoint, setTestingEndpoint] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/debug');
      setDados(data.checks);
    } catch (err) {
      toast.error('Erro ao carregar debug: ' + (err.response?.data?.erro || err.message));
    }
    setLoading(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const testarBD = async () => {
    setTestingDb(true);
    setDbResult(null);
    try {
      const { data } = await api.post('/admin/debug/testar-db');
      setDbResult({ ok: true, ...data });
      toast.success('✅ Banco de dados respondendo!');
    } catch (err) {
      setDbResult({ ok: false, erro: err.response?.data?.erro || err.message });
      toast.error('Erro na conexão com BD.');
    }
    setTestingDb(false);
  };

  const testarEmail = async () => {
    if (!testEmail) return toast.error('Informe um e-mail para teste.');
    setSendingEmail(true);
    try {
      const { data } = await api.post('/admin/debug/testar-email', { email: testEmail });
      toast.success(data.mensagem || 'E-mail enviado!');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Falha ao enviar e-mail.');
    }
    setSendingEmail(false);
  };

  const testarEndpoint = async () => {
    if (!endpoint) return;
    setTestingEndpoint(true);
    setEndpointRes(null);
    const inicio = Date.now();
    try {
      const { data, status } = await api.get(endpoint.startsWith('/') ? endpoint : '/' + endpoint);
      setEndpointRes({ ok: true, status, ms: Date.now() - inicio, data });
    } catch (err) {
      setEndpointRes({
        ok: false,
        status: err.response?.status,
        ms: Date.now() - inicio,
        data: err.response?.data || { erro: err.message },
      });
    }
    setTestingEndpoint(false);
  };

  if (loading) {
    return (
      <Layout title="Debug do Sistema" subtitle="Diagnóstico técnico da plataforma">
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-acre-green border-t-transparent rounded-full animate-spin"/>
        </div>
      </Layout>
    );
  }

  const amb = dados?.ambiente || {};
  const banco = dados?.banco || {};

  return (
    <Layout title="Debug do Sistema" subtitle="Diagnóstico técnico da plataforma">

      {/* Barra de ação */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${dados ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}/>
          <span className="text-sm font-semibold text-gray-700">Sistema {dados ? 'Online' : 'Com problemas'}</span>
        </div>
        <button onClick={carregar} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-acre-green border border-gray-200 hover:border-acre-green px-3 py-1.5 rounded-lg transition-colors">
          <RefreshCw size={12}/> Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">

        {/* Ambiente */}
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Server size={15} className="text-acre-green"/>
            <h2 className="font-sora font-bold text-sm text-acre-dark">Ambiente</h2>
          </div>
          <InfoRow label="Node ENV"    value={amb.node_env} />
          <InfoRow label="Porta"       value={amb.porta} />
          <InfoRow label="Uptime"      value={`${Math.floor(amb.uptime_s / 60)}min ${amb.uptime_s % 60}s`} />
          <InfoRow label="Memória RAM" value={`${amb.mem_mb} MB`} />
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <span className="text-xs text-gray-500">Pagar.me</span>
            <StatusBadge ok={amb.pagarme_ok} sim="Configurado" nao="Não configurado" />
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <span className="text-xs text-gray-500">E-mail SMTP</span>
            <StatusBadge ok={amb.email_ok} sim="Configurado" nao="Não configurado" />
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-xs text-gray-500">JWT Secret</span>
            <StatusBadge ok={amb.jwt_ok} sim="Configurado" nao="Ausente" />
          </div>
        </div>

        {/* Banco de dados */}
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Database size={15} className="text-acre-green"/>
            <h2 className="font-sora font-bold text-sm text-acre-dark">Banco de Dados</h2>
          </div>
          <InfoRow label="Candidatos"        value={banco.candidatos} />
          <InfoRow label="Empresas"          value={banco.empresas} />
          <InfoRow label="Vagas abertas"     value={banco.vagas_abertas} />
          <InfoRow label="Matches"           value={banco.matches_total} />
          <InfoRow label="Seleção"           value={banco.selecao_total} />
          <InfoRow label="Empresas ativas"   value={banco.empresas_ativas} />
          <InfoRow label="Pag. pendentes"    value={banco.empresas_pendentes} />
          <InfoRow label="Logs registrados"  value={banco.logs_total} />
          <div className="mt-3">
            <button onClick={testarBD} disabled={testingDb}
              className="flex items-center gap-1.5 text-xs bg-acre-green text-white px-3 py-1.5 rounded-lg hover:bg-acre-mid transition-colors disabled:opacity-60">
              {testingDb ? <Loader2 size={11} className="animate-spin"/> : <Play size={11}/>}
              Testar conexão
            </button>
            {dbResult && (
              <div className={`mt-2 text-xs p-2 rounded-lg font-mono ${dbResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {dbResult.ok ? `✅ ${dbResult.banco} — ${dbResult.agora}` : `❌ ${dbResult.erro}`}
              </div>
            )}
          </div>
        </div>

        {/* Tabelas no banco */}
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <HardDrive size={15} className="text-acre-green"/>
            <h2 className="font-sora font-bold text-sm text-acre-dark">Tabelas ({dados?.tabelas?.length || 0})</h2>
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
            {(dados?.tabelas || []).map(t => (
              <span key={t} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">{t}</span>
            ))}
          </div>
        </div>

        {/* Últimos erros */}
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle size={15} className="text-red-500"/>
            <h2 className="font-sora font-bold text-sm text-acre-dark">Últimos Erros</h2>
          </div>
          {(dados?.ultimos_erros || []).length === 0 ? (
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              <CheckCircle size={12} className="text-green-500"/> Nenhum erro registrado
            </p>
          ) : (
            <div className="space-y-2 max-h-44 overflow-y-auto">
              {dados.ultimos_erros.map((e, i) => (
                <div key={i} className="bg-red-50 border border-red-100 rounded-lg p-2">
                  <p className="text-xs text-red-700 font-medium">{e.mensagem}</p>
                  <p className="text-xs text-red-400 mt-0.5">{new Date(e.created_at).toLocaleString('pt-BR')}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ferramentas de teste */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">

        {/* Teste de e-mail */}
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Mail size={15} className="text-acre-green"/>
            <h2 className="font-sora font-bold text-sm text-acre-dark">Testar E-mail</h2>
          </div>
          <div className="flex gap-2">
            <input
              className="input text-xs flex-1"
              type="email"
              placeholder="email@destino.com"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
            />
            <button onClick={testarEmail} disabled={sendingEmail || !testEmail}
              className="flex items-center gap-1.5 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60 whitespace-nowrap">
              {sendingEmail ? <Loader2 size={11} className="animate-spin"/> : <Play size={11}/>}
              Enviar
            </button>
          </div>
          {!amb.email_ok && (
            <p className="text-xs text-yellow-600 mt-2 flex items-center gap-1">
              <AlertTriangle size={10}/> SMTP não configurado no .env
            </p>
          )}
        </div>

        {/* Teste de endpoint */}
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity size={15} className="text-acre-green"/>
            <h2 className="font-sora font-bold text-sm text-acre-dark">Testar Endpoint GET</h2>
          </div>
          <div className="flex gap-2">
            <input
              className="input text-xs flex-1 font-mono"
              placeholder="/admin/dashboard"
              value={endpoint}
              onChange={e => setEndpoint(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && testarEndpoint()}
            />
            <button onClick={testarEndpoint} disabled={testingEndpoint || !endpoint}
              className="flex items-center gap-1.5 text-xs bg-acre-green text-white px-3 py-1.5 rounded-lg hover:bg-acre-mid transition-colors disabled:opacity-60 whitespace-nowrap">
              {testingEndpoint ? <Loader2 size={11} className="animate-spin"/> : <Play size={11}/>}
              Testar
            </button>
          </div>
          {endpointRes && (
            <div className={`mt-2 rounded-lg border text-xs ${endpointRes.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className={`flex items-center gap-2 px-2 py-1.5 border-b ${endpointRes.ok ? 'border-green-200 text-green-700' : 'border-red-200 text-red-700'}`}>
                <span className="font-bold">HTTP {endpointRes.status}</span>
                <span className="text-gray-400">{endpointRes.ms}ms</span>
              </div>
              <pre className="p-2 overflow-x-auto max-h-28 font-mono text-gray-600 text-[10px]">
                {JSON.stringify(endpointRes.data, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>

      {/* Usuários recentes */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Cpu size={15} className="text-acre-green"/>
          <h2 className="font-sora font-bold text-sm text-acre-dark">Últimos Usuários Cadastrados</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left text-xs font-semibold text-gray-500 px-3 py-2">Nome</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-3 py-2">E-mail</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-3 py-2">Tipo</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-3 py-2">Pagamento</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-3 py-2">Cadastro</th>
              </tr>
            </thead>
            <tbody>
              {(dados?.usuarios_recentes || []).map((u, i) => (
                <tr key={i} className="border-t border-gray-50">
                  <td className="px-3 py-2 text-xs font-medium text-gray-700">{u.nome}</td>
                  <td className="px-3 py-2 text-xs text-gray-500 font-mono">{u.email}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                      u.tipo === 'admin' ? 'bg-purple-100 text-purple-700' :
                      u.tipo === 'empresa' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>{u.tipo}</span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      u.status_pagamento === 'ativo' ? 'bg-green-50 text-green-600' :
                      u.status_pagamento === 'pendente' ? 'bg-yellow-50 text-yellow-600' : 'bg-gray-50 text-gray-500'
                    }`}>{u.status_pagamento || '—'}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400">
                    {new Date(u.created_at).toLocaleDateString('pt-BR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
